import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import DeckModal from "@/components/DeckModal";
import { fetchDeckById, fetchCardsByDeckId } from "@/lib/db";
import type { Deck } from "@/types/linguadeck";

export type EditDeckPageProps = {
  updateDeck: (deckId: string, data: Partial<Deck>) => Promise<void>;
};

export default function EditDeckPage({ updateDeck }: EditDeckPageProps) {
  const navigate = useNavigate();
  const { deckId } = useParams<{ deckId: string }>();
  const { t } = useTranslation();
  const [deck, setDeck] = useState<Deck | null>(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [showValidationErrors, setShowValidationErrors] = useState(false);

  useEffect(() => {
    if (!deckId) {
      setNotFound(true);
      setLoading(false);
      return;
    }
    fetchDeckById(deckId)
      .then((d) => {
        if (!d) setNotFound(true);
        else setDeck(d);
      })
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
  }, [deckId]);

  const handleSave = async (
    data: Omit<Deck, "id">,
    options?: { isFromAi?: boolean; navigateToStudy?: boolean }
  ) => {
    if (!deckId) return;
    try {
      await updateDeck(deckId, data);
      toast.success(t("deckModal.saveDeck") + " ✅");
      if (options?.navigateToStudy !== false) {
        // CRITICAL: Fetch freshly saved cards from Supabase for TTS (real DB ids + frontLang/backLang)
        let deckForStudy: Deck = { ...data, id: deckId };
        try {
          const dbCards = await fetchCardsByDeckId(deckId);
          if (dbCards.length > 0) {
            deckForStudy = { ...deckForStudy, cards: dbCards };
          }
        } catch (e) {
          console.warn("[EditDeckPage] fetchCardsByDeckId failed:", e);
        }
        setTimeout(() => {
          navigate(`/library/study/${deckId}?mode=fullscreen`, { state: { deck: deckForStudy } });
        }, 150);
      } else {
        navigate("/library", { state: { focusDeckId: deckId, scrollToTop: true } });
      }
    } catch (err: unknown) {
      console.error("DB Error:", err);
      toast.error(t("deckModal.generationError"));
    }
  };

  if (loading) {
    return (
      <div
        style={{
          minHeight: "60vh",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          color: "hsl(var(--muted-foreground))",
        }}
      >
        <div style={{ fontSize: 48, marginBottom: 16 }}>🎴</div>
        <div>{t("folder.loading")}</div>
      </div>
    );
  }

  if (notFound || !deck) {
    return (
      <div
        style={{
          minHeight: "60vh",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          color: "hsl(var(--muted-foreground))",
        }}
      >
        <div style={{ fontSize: 48, marginBottom: 16 }}>🔒</div>
        <div style={{ marginBottom: 16 }}>{t("shared.deckNotFound")}</div>
        <button
          onClick={() => navigate("/library")}
          style={{
            padding: "10px 20px",
            borderRadius: 50,
            border: "none",
            background: "hsl(var(--primary))",
            color: "white",
            fontSize: 14,
            cursor: "pointer",
          }}
        >
          {t("editDeck.backToLibrary")}
        </button>
      </div>
    );
  }

  return (
    <DeckModal
      deck={deck}
      asPage
      initialType={(deck.type as "words" | "cards" | "tests") ?? "cards"}
      onSave={handleSave}
      onClose={() => navigate(-1)}
      showValidationErrors={showValidationErrors}
      setShowValidationErrors={setShowValidationErrors}
    />
  );
}
